// src/components/CategoryButton.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const CategoryButton = ({ category, onClick, isSelected = false }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    console.log('🎨 CategoryButton rendered:', {
      name: category.name,
      image: category.image,
      subCategories: category.subCategories?.length
    });
  }

  // Use local image path from categories data
  const imageUrl = category.image;

  // Handle image load
  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);

    // Log only in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ Image loaded: ${category.name} from ${imageUrl}`);
    }
  };

  // Handle image error
  const handleImageError = (e) => {
    setImageError(true);
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ Image failed: ${category.name} from ${imageUrl}`, e);
    }
  };

  const handleClick = () => {
    console.log('🎯 CategoryButton clicked:', category.name);
    navigate(`/subcategory/${category.name}`);
  };

  // Debug logging
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🎨 CategoryButton for ${category.name}:`, {
        image: category.image,
        imageLoaded
      });
    }
  }, [category, imageLoaded]);

  return (
    <button
      onClick={handleClick}
      className="w-full h-24 relative rounded-2xl shadow-lg overflow-hidden group transition-all duration-500 hover:shadow-2xl hover:scale-[1.03] active:scale-95 cursor-pointer border border-white/10"
    >
      {/* Background Image with more subtle dimming */}
      <img
        src={imageUrl}
        alt={`${category.name} category`}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        onLoad={handleImageLoad}
        onError={handleImageError}
      />

      {/* Dynamic Glass Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent transition-opacity duration-500 group-hover:opacity-80" />

      {/* Content Container */}
      <div className="relative z-10 flex items-center justify-between h-full px-8">
        <div className="flex flex-col items-start translate-y-1 group-hover:translate-y-0 transition-transform duration-500">
          <span className="text-white text-2xl font-bold tracking-tight drop-shadow-md font-outfit">
            {category.name}
          </span>
          <div className="flex items-center space-x-2 opacity-80 group-hover:opacity-100 transition-opacity">
            <span className="text-white/90 text-[11px] font-semibold uppercase tracking-widest font-inter">
              {category.subCategories?.length || 0} Collections
            </span>
            <div className="w-1 h-1 bg-primary rounded-full" />
          </div>
        </div>

        {/* Floating Indicator */}
        <div className="w-10 h-10 rounded-full glass-dark flex items-center justify-center opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>

      {/* Inner Rim Light */}
      <div className="absolute inset-0 rounded-2xl border border-white/20 pointer-events-none group-hover:border-white/40 transition-colors" />
    </button>
  );
};

export default CategoryButton;
